import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Customers from "@/pages/Customers";
import Services from "@/pages/Services";
import Staff from "@/pages/Staff";
import StaffDetail from "@/pages/StaffDetail";
import POS from "@/pages/POS";
import Memberships from "@/pages/Memberships";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicBooking from "@/pages/PublicBooking";
import BookingHome from "@/pages/BookingHome";
import PlatformLayout from "@/components/platform/PlatformLayout";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformTenants from "@/pages/platform/PlatformTenants";
import PlatformTenantDetail from "@/pages/platform/PlatformTenantDetail";
import PlatformPlans from "@/pages/platform/PlatformPlans";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import PlatformAnalytics from "@/pages/platform/PlatformAnalytics";
import PlatformRevenue from "@/pages/platform/PlatformRevenue";
import PlatformSettings from "@/pages/platform/PlatformSettings";
import PlatformSubscriptions from "@/pages/platform/PlatformSubscriptions";
import PlatformActivity from "@/pages/platform/PlatformActivity";
import PlatformBlog from "@/pages/platform/PlatformBlog";
import PlatformBlogEditor from "@/pages/platform/PlatformBlogEditor";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Billing from "@/pages/Billing";
import { brand, isBookingDomain, SAAS_ORIGIN } from "@/lib/site";
import { canAccess, defaultHome } from "@/lib/permissions";

function Protected({ children }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading || user === null)
        return (
            <div className="min-h-screen flex items-center justify-center text-secondary">
                <div className="font-serif text-2xl">Loading…</div>
            </div>
        );
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    return children;
}

function RoleRoute({ section, children }) {
    const { user } = useAuth();
    if (user?.role === "super_admin") return <Navigate to="/platform" replace />;
    if (!canAccess(user?.role, section)) {
        return <Navigate to={`/app/${defaultHome(user?.role)}`} replace />;
    }
    return children;
}

function AppHome() {
    const { user } = useAuth();
    if (user?.role === "super_admin") return <Navigate to="/platform" replace />;
    return <Navigate to={defaultHome(user?.role)} replace />;
}

function SuperAdminProtected({ children }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading || user === null)
        return (
            <div className="min-h-screen flex items-center justify-center text-secondary">
                <div className="font-serif text-2xl">Loading…</div>
            </div>
        );
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    if (user.role !== "super_admin") return <Navigate to="/app/dashboard" replace />;
    return children;
}

function ScrollTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        // Must not return anything: Chrome's window.scrollTo() now returns a
        // Promise, and React 19 treats any non-undefined effect return value as
        // the cleanup function — calling it on the next route change threw and
        // unmounted the whole tree (blank page until a reload).
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function SiteTitle() {
    useEffect(() => {
        document.title = `${brand.name} | ${brand.tagline}`;
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
        }
        link.type = "image/svg+xml";
        link.href = brand.logo;
    }, []);
    return null;
}

// On the consumer booking domain, the owner/operator area lives on the SaaS
// domain — bounce these routes across instead of rendering them here.
function ToSaaS({ path }) {
    const loc = useLocation();
    const target = path ?? `${loc.pathname}${loc.search}`;
    useEffect(() => {
        window.location.replace(`${SAAS_ORIGIN}${target}`);
    }, [target]);
    return null;
}

// bookmysaloon.com — consumer booking site only.
function BookingRoutes() {
    return (
        <Routes>
            <Route path="/" element={<BookingHome />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route path="/login" element={<ToSaaS path="/login" />} />
            <Route path="/register" element={<ToSaaS path="/register" />} />
            <Route path="/forgot-password" element={<ToSaaS />} />
            <Route path="/reset-password" element={<ToSaaS />} />
            <Route path="/app/*" element={<ToSaaS path="/app" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// websalon.in (and local dev) — full SaaS operations product.
function SaasRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route
                path="/platform"
                element={
                    <SuperAdminProtected>
                        <PlatformLayout />
                    </SuperAdminProtected>
                }
            >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<PlatformDashboard />} />
                <Route path="salons" element={<PlatformTenants />} />
                <Route path="salons/:tenantId" element={<PlatformTenantDetail />} />
                <Route path="subscriptions" element={<PlatformSubscriptions />} />
                <Route path="plans" element={<PlatformPlans />} />
                <Route path="billing" element={<PlatformBilling />} />
                <Route path="users" element={<PlatformUsers />} />
                <Route path="analytics" element={<PlatformAnalytics />} />
                <Route path="revenue" element={<PlatformRevenue />} />
                <Route path="activity" element={<PlatformActivity />} />
                <Route path="blog" element={<PlatformBlog />} />
                <Route path="blog/:postId" element={<PlatformBlogEditor />} />
                <Route path="settings" element={<PlatformSettings />} />
            </Route>

            <Route
                path="/app"
                element={
                    <Protected>
                        <AppLayout />
                    </Protected>
                }
            >
                <Route index element={<AppHome />} />
                <Route path="dashboard" element={<RoleRoute section="dashboard"><Dashboard /></RoleRoute>} />
                <Route path="calendar" element={<RoleRoute section="calendar"><CalendarPage /></RoleRoute>} />
                <Route path="customers" element={<RoleRoute section="customers"><Customers /></RoleRoute>} />
                <Route path="services" element={<RoleRoute section="services"><Services /></RoleRoute>} />
                <Route path="staff" element={<RoleRoute section="staff"><Staff /></RoleRoute>} />
                <Route path="staff/:staffId" element={<RoleRoute section="staff"><StaffDetail /></RoleRoute>} />
                <Route path="pos" element={<RoleRoute section="pos"><POS /></RoleRoute>} />
                <Route path="memberships" element={<RoleRoute section="memberships"><Memberships /></RoleRoute>} />
                <Route path="reports" element={<RoleRoute section="reports"><Reports /></RoleRoute>} />
                <Route path="billing" element={<RoleRoute section="billing"><Billing /></RoleRoute>} />
                <Route path="settings" element={<RoleRoute section="settings"><Settings /></RoleRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" richColors />
                    <ScrollTop />
                    <SiteTitle />
                    {isBookingDomain ? <BookingRoutes /> : <SaasRoutes />}
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
